/* eslint-disable clubmed/no-fake-component */
import { get, noop } from 'lodash';
import { useRouter } from 'next/router';
import React, { Fragment, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import URI from 'urijs';
import { appQueryParameters } from '@clubmed/utils';
import {
  deleteAdditionalServices,
  addAdditionalService,
} from 'Store/booking/proposals/proposals.action';
import { selectProposal } from 'Store/booking/selected-proposal-id/selectedProposalId.action';
import { useAppContext } from 'Context/ReactAppContext';
import { AlternativePackages } from 'Components/widgets/package/alternative-packages/AlternativePackages';
import type { OverloadForSpecificProposalIdsType } from 'Utils/graphql-query-helper/booking/proposal/proposalQuery';
import 'Utils/graphql-query-helper/booking/proposal/proposalQuery';
import {
  hasChildrenAgedXOrLess,
  getCurrentKid,
} from 'Components/widgets/childcare/childcare.helper';
import { Childcare } from 'Components/widgets/childcare/Childcare';
import { ChildcareIncludedEquipmentCapsule } from 'Components/widgets/childcare/ChildcareIncludedEquipmentCapsule';
import { ChildcareOnDemandEquipmentCapsule } from 'Components/widgets/childcare/ChildcareOnDemandEquipmentCapsule';
import { renderInfoBanner } from 'Components/widgets/info-banner/infoBanner.utils';
import type { InfoBannerData } from 'Components/widgets/info-banner/infoBanner.query';
import 'Components/widgets/info-banner/infoBanner.query';
import { tripInformationSelector } from 'Store/trip-information/storeHelper';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { trackErrorOnAddService } from 'Views/booking/BookingEngine.utils';
import { BE_GA_DEFAULT_PAYLOAD } from 'Views/booking/constants';
import { useBookingNavbar } from 'Components/widgets/booking-navbar/useBookingNavbar';
import { useChidcareContext } from 'Src/hooks/use-childcare-context/useChildcareContext';
import { ChildcareContext } from 'Context/ChildcareContext';
import { ServicesContext } from 'Context/ServicesContext';
import { selectAttendeesWithInfosByHouseholds } from 'Store/booking/attendees/attendees.selector';
import { asInfoBannerArgs, makeViewProps } from 'Src/types/view-helpers';
type Props = {
  proposals: ReadonlyArray<Proposal>;
  resort: Product;
  selectedProposal: Proposal;
  infoBannerData: InfoBannerData;
};

const ServicesView = ({ selectedProposal, resort, proposals, infoBannerData }: Props) => {
  useBookingNavbar({
    noEdit: false,
  });
  const appContext = useAppContext();
  const trackingDefaultPayload = {
    actionGA: 'services',
  };
  const dispatch = useDispatch();
  const router = useRouter();
  const tripInformation = useSelector(tripInformationSelector);
  const trackError = useTracking({ ...BE_GA_DEFAULT_PAYLOAD, actionGA: 'service' });
  const { attendees: attendeesWithInfos } = useSelector(selectAttendeesWithInfosByHouseholds);
  const attendeesList = [
    ...attendeesWithInfos.adults,
    ...attendeesWithInfos.children.map((child) => ({ ...child, isChild: true })),
  ];
  const { childcareContextServicesValuesBE } = useChidcareContext({
    appContext,
    proposals,
  });
  const servicesContextValuesBE = useMemo(
    () =>
      makeViewProps<any>({
        attendeesList,
        arrivalDate: selectedProposal.arrivalDate,
        departureDate: selectedProposal.departureDate,
        showRentAllSchedules: true,
        updateService: noop,
      }),
    [selectedProposal, attendeesList],
  );

  const onAddChildcare = ({
    childcareId,
    attendeeId,
    startDate,
    endDate,
    overloadChildcareIdForChalet,
  }: {
    childcareId: string;
    attendeeId: string;
    startDate: string;
    endDate: string;
    overloadChildcareIdForChalet?: OverloadForSpecificProposalIdsType;
  }) => {
    const addChildcareAction = addAdditionalService({
      proposalIds: proposals.map(({ id }) => id),
      serviceId: childcareId,
      attendeeIds: attendeeId,
      schedules: [
        {
          startDate,
          endDate,
        },
      ],
      overloadForSpecificProposalIds: overloadChildcareIdForChalet,
      appContext,
    });
    return dispatch(addChildcareAction).catch((error) =>
      trackErrorOnAddService(error, trackError, 'UNKNOWN_CHILDCARE'),
    );
  };

  const onDeleteChildcare = ({
    childcareId,
    attendeeId,
    overloadChildcareIdForChalet,
  }: {
    childcareId: string;
    attendeeId: string;
    overloadChildcareIdForChalet?: OverloadForSpecificProposalIdsType;
  }) => {
    return dispatch(
      deleteAdditionalServices({
        serviceIds: [childcareId],
        attendeeIds: attendeeId,
        overloadForSpecificProposalIds: overloadChildcareIdForChalet,
        appContext,
      }),
    );
  };

  const dispatchSelectProposal = (proposalId) => {
    const updatedUri = new URI(router.asPath).setQuery(
      appQueryParameters.booking.selectedProposalId,
      proposalId,
    );
    router.replace(updatedUri.toString());
    return dispatch(selectProposal(proposalId));
  };

  const renderJoyView = () => (
    <AlternativePackages
      selectedProposal={selectedProposal}
      selectProposal={dispatchSelectProposal}
    />
  );

  const renderChildcare = () => {
    const childrenAttendees = get(selectedProposal, 'attendees.children');
    const includedChildrenActivities = resort.childrenActivities.filter(
      (activity) => activity.equipments.length > 1,
    );
    const onDemandChildrenActivities = resort.childrenActivities.filter(
      (activity) => !activity.isIncluded,
    );
    const shouldShowIncludedEquipments = hasChildrenAgedXOrLess(childrenAttendees, 6);
    return (
      <div className="-mx-10 flex flex-wrap">
        <div className="w-full p-10 sm:w-2/3 md:w-3/4">
          <ChildcareContext.Provider value={childcareContextServicesValuesBE}>
            <ServicesContext.Provider value={servicesContextValuesBE}>
              <Childcare
                addChildcare={onAddChildcare}
                attendees={selectedProposal.attendees.children.map((child) => {
                  const referedChild = getCurrentKid(tripInformation.kids, child);
                  return makeViewProps<any>({
                    ...child,
                    firstName: referedChild && referedChild.firstName,
                    lastName: referedChild && referedChild.lastName,
                  });
                })}
                childcareAdditionalServices={selectedProposal.additionalServices.childcares}
                deleteChildcare={onDeleteChildcare}
                proposals={proposals}
              />
            </ServicesContext.Provider>
          </ChildcareContext.Provider>
        </div>
        <div className="w-full p-10 sm:order-first sm:w-1/3 md:w-1/4">
          {shouldShowIncludedEquipments && (
            <ChildcareIncludedEquipmentCapsule
              className="mb-20"
              facilities={makeViewProps<any>([...includedChildrenActivities, ...resort.facilities])}
            />
          )}
          <ChildcareOnDemandEquipmentCapsule activities={onDemandChildrenActivities} />
        </div>
      </div>
    );
  };

  return (
    <Fragment>
      {!(resort.isJoyview || resort.isUrbanOasis) &&
        renderInfoBanner(
          asInfoBannerArgs({
            infoBannerData,
            departureDate: selectedProposal.departureDate,
            arrivalDate: selectedProposal.arrivalDate,
            currentStep: 'services',
            ...trackingDefaultPayload,
          }),
        )}
      {resort.isJoyview || resort.isUrbanOasis ? renderJoyView() : renderChildcare()}
    </Fragment>
  );
};

export default ServicesView;
